import { DotaRole, useFetchTeamPlayers } from "@portal-frontend-ssr/blast-api";
import { TournamentTeamSlot } from "@portal-frontend-ssr/blast-api";
import { generateImageUrl } from "@portal-frontend-ssr/blast-assets-api";
import { AdaptiveLink, FlagCountryCode, FlagIcon } from "@portal-frontend-ssr/ui";
import classNames from "classnames";
import { useMemo } from "react";
import { ClientOnly } from "remix-utils/client-only";

interface TournamentTeamProps {
  tournamentTeamSlot?: TournamentTeamSlot;
  showLineup?: boolean;
  disableLinkToTeamPage?: boolean;
}

// To sort the players by dota role priority
const rolePriority: Record<DotaRole, number> = {
  carry: 1,
  mid: 2,
  offlane: 3,
  "soft-support": 4,
  "hard-support": 5,
};

/**
 * Component used to render link redirecting to match page, if the match page url is available
 */
const TournamentTeamWrapper = ({
  condition,
  wrapper,
  children,
}: {
  condition: boolean;
  wrapper: (children: React.JSX.Element) => React.JSX.Element;
  children: React.JSX.Element;
}): React.JSX.Element => {
  if (condition) {
    return wrapper(children);
  } else {
    return children;
  }
};

export const TournamentTeam = ({ tournamentTeamSlot, showLineup, disableLinkToTeamPage }: TournamentTeamProps) => {
  const { team, qualificationMethod } = tournamentTeamSlot ?? {};

  return (
    <TournamentTeamWrapper
      condition={!disableLinkToTeamPage}
      wrapper={(children) => (
        <AdaptiveLink to={team ? `/teams/${team.shortName?.toLowerCase() || team.name.toLowerCase()}` : "#"}>
          {children}
        </AdaptiveLink>
      )}
    >
      <div
        className={classNames(
          "outline-hidden group relative flex w-auto grid-cols-2 items-center justify-center overflow-hidden rounded-small border-none bg-background-95 px-2 py-3 text-white transition-all md:py-4",
          {
            "cursor-pointer": !disableLinkToTeamPage,
          },
        )}
      >
        <div className="flex flex-col items-center justify-center gap-2">
          {team ? (
            <img
              className="h-5 object-contain md:h-8"
              src={generateImageUrl("teams", team.id, {
                format: "auto",
                height: "40",
              })}
              alt={team.name}
            />
          ) : (
            <div className="flex size-8 items-center justify-center rounded-full bg-background-80 text-white/90 font-style-body-b2">
              ?
            </div>
          )}
          <div className="text-center capitalize text-white font-style-body-b4 md:font-style-body-b2">
            {team?.name ?? "TBD"}
          </div>
          {!!qualificationMethod && (
            <div className="text-center text-neutral font-style-body-b4 md:font-style-body-b3">
              {qualificationMethod}
            </div>
          )}
        </div>
        <ClientOnly>
          {() => {
            if (!team) return null;
            return <RosterOverlay team={team} isShowing={showLineup} />;
          }}
        </ClientOnly>
      </div>
    </TournamentTeamWrapper>
  );
};

const RosterOverlay = ({
  team,
  isShowing,
}: {
  team: {
    id: string;
    name: string;
  };
  isShowing?: boolean;
}) => {
  const { data: players, isLoading } = useFetchTeamPlayers(team.id);

  const activeRoster = useMemo(() => {
    if (!players || isLoading) return [];

    const filteredPlayers = players.filter((player) => player.role === "player");

    return filteredPlayers
      .sort((a, b) => {
        if (!a.dotaMetadata?.role && !b.dotaMetadata?.role) return 0;
        if (!a.dotaMetadata?.role) return 1; // Place a at the end
        if (!b.dotaMetadata?.role) return -1; // Place b at the end

        // Sort based on role priority
        return (rolePriority[a.dotaMetadata.role] || 0) - (rolePriority[b.dotaMetadata.role] || 0);
      })
      .slice(0, 5);
  }, [players, isLoading]);

  return (
    <div
      className={classNames(
        "absolute flex size-full items-center justify-center bg-background-95 opacity-0 transition-all duration-500 group-hover:opacity-100",
        {
          "opacity-100": isShowing,
        },
      )}
    >
      <div className="relative flex size-full w-full flex-col justify-center">
        <img
          className="absolute left-1/2 top-1/2 h-4/5 w-3/4 -translate-x-1/2 -translate-y-1/2 object-contain opacity-15"
          alt={team.name}
          src={generateImageUrl("teams", team.id, {
            format: "auto",
            height: 140 + "px",
          })}
        />
        <table className="mx-auto w-fit">
          <tbody>
            {activeRoster.map((player, index) => (
              <tr key={index} className="">
                <td className="pr-2">
                  <FlagIcon
                    countryCode={player.nationality as FlagCountryCode}
                    widthPx={16}
                    className="origin-right scale-75 md:scale-100"
                  />
                </td>
                <td className="w-fit max-w-12 truncate whitespace-nowrap text-center uppercase font-style-label-l4 md:max-w-28 md:py-0.5 lg:max-w-40">
                  {player.nickname}
                </td>
              </tr>
            ))}
            {isLoading &&
              Array.from({ length: 5 }).map((_, index) => {
                const rndWidth = Math.floor(Math.random() * 48) + 48;
                return (
                  <tr key={index} className="">
                    <td className="pr-2">
                      <div className="h-3 w-4 animate-pulse bg-background-80"></div>
                    </td>
                    <td className="py-0.5">
                      <div
                        className="mx-auto h-3 animate-pulse rounded-full bg-background-80"
                        style={{ width: rndWidth + "px" }}
                      ></div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
